import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Paper,
  Tabs,
  Tab,
  Stack,
  Divider,
  Grid,
  Avatar,
  IconButton,
  Button,
} from '@mui/material';
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Flag as FlagIcon,
  Category as CategoryIcon,
  Link as LinkIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  AssignmentTurnedIn as CompletedIcon,
  Pending as PendingIcon,
  Cancel as CancelledIcon,
  Pause as OnHoldIcon,
} from '@mui/icons-material';
import { formatDate, formatDateTime } from '../../../utils/formatters.js';

const priorityColors = {
  Low: 'default',
  Medium: 'warning',
  High: 'error',
  Urgent: 'error',
};

const statusColors = {
  Pending: 'warning',
  'In Progress': 'info',
  Completed: 'success',
  Cancelled: 'error',
  'On Hold': 'default',
};

const typeColors = {
  Call: 'primary',
  Meeting: 'secondary',
  Email: 'info',
  FollowUp: 'warning',
  Research: 'default',
  Other: 'default',
};

const getStatusIcon = (status) => {
  switch (status) {
    case 'Completed':
      return <CompletedIcon color="success" />;
    case 'Pending':
      return <PendingIcon color="warning" />;
    case 'Cancelled':
      return <CancelledIcon color="error" />;
    case 'On Hold':
      return <OnHoldIcon color="default" />;
    default:
      return <PendingIcon color="info" />;
  }
};

function TaskDetail({ task, onEdit, onDelete }) {
  const [tabValue, setTabValue] = React.useState(0);

  if (!task) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h6" color="text.secondary">
          Select a task to view details
        </Typography>
      </Box>
    );
  }

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 2 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              {task.title}
            </Typography>
            <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
              <Chip
                icon={getStatusIcon(task.status)}
                label={task.status}
                color={statusColors[task.status] || 'default'}
                size="small"
              />
              <Chip
                label={task.type}
                color={typeColors[task.type] || 'default'}
                variant="outlined"
                size="small"
              />
              <Chip
                icon={<FlagIcon />}
                label={task.priority}
                color={priorityColors[task.priority] || 'default'}
                size="small"
              />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            <IconButton onClick={() => onEdit(task)} color="primary">
              <EditIcon />
            </IconButton>
            <IconButton onClick={() => onDelete(task)} color="error">
              <DeleteIcon />
            </IconButton>
          </Stack>
        </Stack>

        {task.notes && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {task.notes}
          </Typography>
        )}
      </Paper>

      {/* Tabs */}
      <Paper sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" />
          <Tab label="Timeline" />
          <Tab label="Notes" />
        </Tabs>

        {/* Tab Content */}
        <Box sx={{ flex: 1, p: 3, overflow: 'auto' }}>
          {tabValue === 0 && (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Due Date
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <ScheduleIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.dueDate ? formatDate(task.dueDate) : 'Not set'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Assigned To
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <AssignmentIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.assignedTo || 'Unassigned'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Type
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CategoryIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.type || 'Not specified'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12} md={6}>
                <Stack spacing={2}>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Status
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      {getStatusIcon(task.status)}
                      <Typography variant="body2">
                        {task.status}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Priority
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <FlagIcon fontSize="small" color={priorityColors[task.priority] || 'default'} />
                      <Typography variant="body2">
                        {task.priority || 'Not set'}
                      </Typography>
                    </Stack>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Related To
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LinkIcon fontSize="small" color="action" />
                      <Typography variant="body2">
                        {task.related || 'Not specified'}
                      </Typography>
                    </Stack>
                  </Box>
                </Stack>
              </Grid>

              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    Created
                  </Typography>
                  <Typography variant="body2">
                    {task.createdAt ? formatDateTime(task.createdAt) : 'Unknown'}
                  </Typography>
                </Box>
                {task.updatedAt && task.updatedAt !== task.createdAt && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Last Updated
                    </Typography>
                    <Typography variant="body2">
                      {formatDateTime(task.updatedAt)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}

          {tabValue === 1 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Task Timeline
              </Typography>
              <Stack spacing={2}>
                <Paper sx={{ p: 2 }}>
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                      <AssignmentIcon fontSize="small" />
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight="medium">
                        Task Created
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {task.createdAt ? formatDateTime(task.createdAt) : 'Unknown'}
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>

                {task.updatedAt && task.updatedAt !== task.createdAt && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'info.main', width: 32, height: 32 }}>
                        <EditIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Task Updated
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(task.updatedAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}

                {task.dueDate && (
                  <Paper sx={{ p: 2 }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                      <Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
                        <ScheduleIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="medium">
                          Due Date Set
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(task.dueDate)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Paper>
                )}
              </Stack>
            </Box>
          )}

          {tabValue === 2 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Notes & Comments
              </Typography>
              {task.notes ? (
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {task.notes}
                  </Typography>
                </Paper>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No notes available for this task.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default TaskDetail;
